import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Download, Search, Eye, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import TransactionDetailView from "../components/transaction-detail-view";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { api } from "../lib/api";
import { useToast } from "@/hooks/use-toast";
import { normalizeWeightToTons } from "../lib/utils";

export default function InternalWeighbridge() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const { toast } = useToast();


  const { data: sessions, isLoading, refetch } = useQuery({
    queryKey: ["/api/sessions/email"],
    queryFn: async () => {
      const response = await fetch("/api/sessions/email", {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-Id': localStorage.getItem('tenant_id') || '',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return api.getSessions();
        }
        throw new Error('Failed to fetch sessions from all mines');
      }
      
      return response.json();
    },
  });

  const allTransactions = (sessions || []).map((session: any) => ({
    id: session.id,
    transactionNo: session.id?.toString() || "N/A",
    date: session.createdAt ? new Date(session.createdAt).toLocaleDateString() : "N/A",
    time: session.createdAt ? new Date(session.createdAt).toLocaleTimeString() : "N/A",
    orderNo: session.orderExternalId || session.clientOrderNumber || "N/A",
    ticketNo: session.ticketNo || "N/A",
    transactionType: session.entryMethod === "manual" ? "MANUAL" : "AUTO",
    
    // Vehicle information
    vehicleReg: session.vehiclePlateNumber || session.plateNumber || "N/A",
    trailer1PlateNumber: session.trailer1PlateNumber,
    trailer2PlateNumber: session.trailer2PlateNumber,
    
    driverName: session.driverName || "Unknown",
    driverId: session.driverId,
    tareWeight: parseFloat(session.tareWeight || "0"),
    grossWeight: parseFloat(session.grossWeight || "0"),
    netWeight: parseFloat(session.netWeight || "0"),
    unit: session.unit || "kg",
    weighbridgeName: session.weighbridgeName || "Main Scale",
    customerName: session.customerName,
    freightCompany: session.customerName,
    transporterName: session.supplier || session.transporterName,
    tenantName: session.tenantName || session.tenantCode || "Unknown Mine",
    
    entryTime: session.entryTime,
    exitTime: session.exitTime,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    shift: session.shift,
    operator: session.operator,
    
    duration: session.endedAt && session.startedAt ? 
      Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000) + " min" : "N/A",
    
    status: session.status || "active",
    visitStatus: session.visitStatus,
    kanbanStatus: session.kanbanStatus,
    accessDecision: session.accessDecision,
    
    supplierExternalId: session.supplierExternalId,
    sealNumber: session.sealNumber,
    weighbridgeEntryWeight: session.weighbridgeEntryWeight,
    weighbridgeExitWeight: session.weighbridgeExitWeight,
    deliveryPointEntryWeight: session.deliveryPointEntryWeight,
    deliveryPointExitWeight: session.deliveryPointExitWeight
  }));

  const allFilteredTransactions = allTransactions.filter(transaction => {
    if (searchTerm && 
        !transaction.transactionNo.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !transaction.vehicleReg.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.driverName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.orderNo.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.tenantName.toLowerCase().includes(searchTerm.toLowerCase())
    ) return false;
    
    if (operatorFilter !== "all" && transaction.operator !== operatorFilter) return false;
    
    if (statusFilter !== "all" && transaction.status !== statusFilter) return false;
    
    return true;
  });

  const totalItems = allFilteredTransactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredTransactions = allFilteredTransactions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, operatorFilter, statusFilter]);

  const operators = Array.from(new Set(allTransactions.map(t => t.operator))).filter(op => op && op !== "Unknown");

  const handleRefresh = async () => {
    await refetch();
    toast({
      title: "Refreshed",
      description: "Internal weighbridge data refreshed successfully",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading internal weighbridge transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Internal Weighbridge</h1>
            <p className="text-muted-foreground">
              Showing transactions from all mine users
              <br />
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} transactions
              {(statusFilter !== "all" || operatorFilter !== "all" || searchTerm) && " (filtered)"}
              {" • Total in system: "}{allTransactions.length}
            </p>
          </div>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-1 items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="input-search-transaction"
              />
            </div>
            
            <Select value={operatorFilter} onValueChange={setOperatorFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Operators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Operators</SelectItem>
                {operators.map(operator => (
                  <SelectItem key={operator} value={operator}>{operator}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Internal Weighbridge Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Mine</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order Number</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Transporter</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Number Plate</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Driver Fullname</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tare</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Gross</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Net</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredTransactions.map((transaction) => {
                    return (
                    <tr key={transaction.id} className="hover:bg-muted/30">
                      <td className="px-3 py-3">
                        <div className="text-sm">
                          {transaction.startedAt ? new Date(transaction.startedAt).toLocaleDateString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleDateString() : transaction.date)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {transaction.endedAt ? new Date(transaction.endedAt).toLocaleTimeString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleTimeString() : transaction.time)}
                        </div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <Badge variant="outline">{transaction.tenantName}</Badge>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-mono">{transaction.orderNo || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm">{transaction.transporterName || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="font-medium text-sm" data-testid={`vehicle-${transaction.id}`}>
                          {transaction.vehicleReg}
                        </div>
                        {transaction.trailer1PlateNumber && (
                          <div className="text-xs text-muted-foreground">T1: {transaction.trailer1PlateNumber}</div>
                        )}
                        {transaction.trailer2PlateNumber && (
                          <div className="text-xs text-muted-foreground">T2: {transaction.trailer2PlateNumber}</div>
                        )}
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-medium">{transaction.driverName}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-mono">{transaction.driverId || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="text-sm font-medium">
                          {transaction.tareWeight ? `${normalizeWeightToTons(transaction.tareWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="text-sm font-medium">
                          {transaction.grossWeight ? `${normalizeWeightToTons(transaction.grossWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="font-bold text-green-600">
                          {transaction.netWeight ? `${normalizeWeightToTons(transaction.netWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <Dialog open={isDetailViewOpen && selectedTransaction?.id === transaction.id} onOpenChange={(open) => {
                          if (!open) {
                            setIsDetailViewOpen(false);
                            setSelectedTransaction(null);
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setIsDetailViewOpen(true);
                              }}
                              data-testid={`button-view-details-${transaction.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Transaction Details & Images</DialogTitle>
                            </DialogHeader>
                            {selectedTransaction && (
                              <TransactionDetailView transaction={selectedTransaction} />
                            )}
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No transactions found matching your search criteria.</p>
              <p className="text-sm mt-2">Try adjusting your filters or search terms.</p>
            </div>
          )}
  
          {totalItems > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Show</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>per page</span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  data-testid="button-previous-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const showPages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                    let endPage = Math.min(totalPages, startPage + showPages - 1);
                    
                    if (endPage - startPage < showPages - 1) {
                      startPage = Math.max(1, endPage - showPages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          variant={1 === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis1" className="px-2">...</span>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(i)}
                          className="w-8 h-8 p-0"
                          data-testid={`button-page-${i}`}
                        >
                          {i}
                        </Button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis2" className="px-2">...</span>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          variant={totalPages === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          className="w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

